import { useEffect, useRef } from 'react';
import { useIsMutating } from '@tanstack/react-query';
import { useCurrentUser } from '@lp/core/hooks/useCurrentUser';
import { CaseBoardPreferences, StatusGroup, useCasesBoardStore, useCasesFiltersStore } from '@/store/cases-board.store';
import { CaseStatus } from '@/api/apis/case';
import { boardStatusList } from '@/constants/case/case.constants';
import { UserBoardFilters, UserBoardSettings, UserPreferences } from '@/api/apis/user';
import { updateUserPreferencesMutationKey } from '@/queries/user';

export const useSetBoardSettings = () => {
  const { data, updateUserPreferences } = useCurrentUser();
  const casesBoardStore = useCasesBoardStore();
  const setFilters = useCasesFiltersStore((store) => store.setFilters);
  const { setBoardPreferences } = casesBoardStore;
  const hasHydrated = useRef(false);
  const isSavingPreferences = useIsMutating({ mutationKey: updateUserPreferencesMutationKey }) > 0;

  useEffect(() => {
    if (data?.userPreferences && !hasHydrated.current && !isSavingPreferences) {
      hasHydrated.current = true;
      const boardPreferences: Record<string, unknown> = {};
      const boardFilters: Record<string, unknown> = {};
      data?.userPreferences.forEach(({ name, value }) => {
        if (value === undefined) return;
        if (Object.values(UserBoardFilters).includes(name as UserBoardFilters)) {
          boardFilters[name] = JSON.parse(value);
        } else if (Object.values(UserBoardSettings).includes(name as UserBoardSettings)) {
          boardPreferences[name] = JSON.parse(value);
        }
      });

      const statuses: CaseStatus[] = (boardPreferences?.statusesOrdered ?? []) as CaseStatus[];
      const groups: StatusGroup[] = (boardPreferences?.groupsOrdered ?? []) as StatusGroup[];
      const hasStaleStatus = (statusesList: CaseStatus[]) =>
        statusesList.some((status) => !boardStatusList.includes(status));
      if (hasStaleStatus(statuses)) {
        updateUserPreferences([{ name: UserPreferences.statusesOrdered, value: JSON.stringify(boardStatusList) }]);
        boardPreferences.statusesOrdered = boardStatusList;
      }
      if (groups.some((group) => hasStaleStatus(group.statuses))) {
        updateUserPreferences([
          { name: UserPreferences.groupsOrdered, value: JSON.stringify([]) },
          { name: UserPreferences.showGroups, value: JSON.stringify(false) },
        ]);
        boardPreferences.groupsOrdered = [];
        boardPreferences.showGroups = false;
      }

      setFilters(boardFilters);
      setBoardPreferences(boardPreferences as CaseBoardPreferences);
    }
  }, [data?.userPreferences, setFilters, setBoardPreferences, updateUserPreferences, isSavingPreferences]);

  return casesBoardStore;
};
